import React, { useState, useCallback } from 'react';
import { DatePicker, Select, Button, Table, Statistic, Row, Col, Card, Icon, Modal, message } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import api from 'api';
import ErrorMessage from 'components/ErrorMessage';

// Preview + generate screen for Contract Renewals, mirroring the Upcoming Contract Invoices
// flow (components/UpcomingContractInvoices). It calls procedures/contracts.js:
//   * previewContractRenewals  (read-only)  -> the list you review here
//   * generateContractRenewals (writes)     -> appends a DRAFT successor per contract, reusing
//                                               the same renewContract plumbing (successor row,
//                                               -NNN suffix, auto-increase). Generation sends
//                                               NOTHING; renewal notices are sent afterward,
//                                               per-draft, from the contract page.
//
// The default run date is TODAY and the default horizon is 3 months, matching "a renewal notice
// sent automatically 3 months before the renewal becomes effective" -- so the list shows every
// auto-renew contract whose current term ends within the next 3 months and hasn't been renewed.
//
// Superuser-only for now, same gate as the RPCs (RPC_generateContractRenewals /
// RPC_previewContractRenewals are granted to 0 roles; superuser bypasses).

const { Option } = Select;

const columns = [
    {title: 'Contract #', dataIndex: 'contract_number', key: 'contract_number',
     render: (v) => <span style={{fontWeight: 600}}>{v}</span>},
    {title: 'Site', dataIndex: 'site', key: 'site',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Site)</span>},
    {title: 'Bill-To', dataIndex: 'bill_to_customer', key: 'bill_to_customer',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Customer)</span>},
    {title: 'Current', dataIndex: 'current_amount', key: 'current_amount', align: 'right',
     render: (v) => `$${numeral(v).format('0,0.00')}`},
    {title: 'Incr %', dataIndex: 'increase_pct', key: 'increase_pct', align: 'right',
     render: (v) => `${numeral(v).format('0.[00]')}%`},
    {title: 'Projected', dataIndex: 'projected_amount', key: 'projected_amount', align: 'right',
     render: (v) => <span style={{fontWeight: 600}}>{`$${numeral(v).format('0,0.00')}`}</span>},
    {title: 'Term Ends', dataIndex: 'expires', key: 'expires',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : ''},
    {title: 'Renewal Term', key: 'renewal_term',
     render: (_, r) => `${r.renewal_start ? moment(r.renewal_start).format('MMM DD, YYYY') : ''}`
        + ` – ${r.renewal_expires ? moment(r.renewal_expires).format('MMM DD, YYYY') : ''}`},
    // The resolved contract-email destination (contract_email -> statement_email -> invoice_email).
    // Empty is flagged in red so the user knows that contract's renewal notice can't be sent.
    {title: 'Contract Email', dataIndex: 'contract_email', key: 'contract_email',
     render: (v) => v || <span style={{color: '#f5222d'}}>(none)</span>},
];

function downloadCsv(csv, asOf) {
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upcoming_contract_renewals_${asOf}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function UpcomingContractRenewals() {
    const [runDate, setRunDate] = useState(moment());
    const [horizon, setHorizon] = useState(3);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    // Read-only preview. Returns the report result on success, or null after setting an error.
    const runPreview = useCallback(async (as_of_date, horizon_months) => {
        const response = await api.rpc('previewContractRenewals', {as_of_date, horizon_months});
        const envelope = (response && response.body) || {};
        if (response && response.success && envelope.result && !envelope.error) {
            return envelope.result;
        }
        const msg = (envelope.error && envelope.error.message)
            || (response && response.errors && response.errors[0] && response.errors[0].message)
            || 'The preview could not be generated.';
        setError(msg);
        return null;
    }, []);

    const preview = useCallback(async () => {
        if (!runDate) {
            setError('Pick a run date first.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await runPreview(runDate.format('YYYY-MM-DD'), horizon);
            setReport(result);
        } catch (e) {
            setError(String(e && e.message ? e.message : e));
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [runDate, horizon, runPreview]);

    // Live generation. Runs against the exact date/horizon that was previewed, then RE-PREVIEWS
    // to refresh the list (generated drafts drop out -- their predecessor's is_renewed is now
    // TRUE -- so the second preview shows what's left).
    const generate = useCallback(async () => {
        if (!report) return;
        const as_of_date = report.as_of_date;
        const horizon_months = report.horizon_months;
        setGenerating(true);
        setError(null);
        try {
            const response = await api.rpc('generateContractRenewals', {as_of_date, horizon_months});
            const envelope = (response && response.body) || {};
            if (response && response.success && envelope.result && !envelope.error) {
                const r = envelope.result;
                message.success(
                    `Generated ${r.generated_count} renewal draft${r.generated_count === 1 ? '' : 's'}. ` +
                    `Open each draft to send its renewal notice.`, 6);
                // Refresh: re-preview the same window so the generated drafts fall off the list.
                const refreshed = await runPreview(as_of_date, horizon_months);
                if (refreshed) setReport(refreshed);
            } else {
                const msg = (envelope.error && envelope.error.message)
                    || (response && response.errors && response.errors[0] && response.errors[0].message)
                    || 'The renewal drafts could not be generated.';
                setError(msg);
            }
        } catch (e) {
            setError(String(e && e.message ? e.message : e));
        } finally {
            setGenerating(false);
        }
    }, [report, runPreview]);

    const confirmGenerate = useCallback(() => {
        if (!report) return;
        Modal.confirm({
            title: 'Generate these renewal drafts?',
            content: `This will create ${report.count} draft renewal contract` +
                `${report.count === 1 ? '' : 's'} (successor terms with the auto-renew increase applied). ` +
                `Drafts are NOT active and send nothing on their own — you send each renewal notice ` +
                `afterward from the draft. Contracts already renewed are skipped.`,
            okText: 'Generate Drafts',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk: generate,
        });
    }, [report, generate]);

    const rows = (report && report.renewals) || [];

    return <div style={{margin: 'auto', maxWidth: '1200px', padding: '24px'}}>
        <h1 style={{fontFamily: 'Raleway'}}>Upcoming Contract Renewals</h1>
        <p style={{color: '#666', maxWidth: '820px'}}>
            Preview every auto-renew contract whose current term ends within the selected window
            and hasn't been renewed yet. Generating creates a DRAFT successor for each (carrying
            the auto-renew increase); nothing is sent. You send each renewal notice afterward,
            from the draft's page.
        </p>

        <div style={{display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '24px', flexWrap: 'wrap'}}>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>As of Date</div>
                <DatePicker
                    value={runDate}
                    onChange={setRunDate}
                    allowClear={false}
                    format="MMM DD, YYYY"
                    style={{width: '180px'}} />
            </div>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>Within</div>
                <Select value={horizon} onChange={setHorizon} style={{width: '150px'}}>
                    <Option value={3}>3 months</Option>
                    <Option value={6}>6 months</Option>
                    <Option value={12}>12 months</Option>
                </Select>
            </div>
            <Button type="primary" icon="calculator" loading={loading} onClick={preview}>
                Preview Renewals
            </Button>
            {report && rows.length > 0 &&
                <Button type="danger" icon="reload" loading={generating} onClick={confirmGenerate}>
                    Generate Renewal Drafts
                </Button>}
            {report && rows.length > 0 &&
                <Button icon="download"
                        onClick={() => downloadCsv(report.csv, report.as_of_date)}>
                    Download CSV
                </Button>}
        </div>

        {error && <ErrorMessage errors={error} />}

        {report && <div>
            <Row gutter={16} style={{marginBottom: '24px'}}>
                <Col span={12}><Card><Statistic title="Contracts Due to Renew" value={report.count} /></Card></Col>
                <Col span={12}><Card><Statistic title="Projected Renewal Total"
                    value={report.total_projected_amount} precision={2} prefix="$" /></Card></Col>
            </Row>

            {rows.length === 0
                ? <Card style={{textAlign: 'center', color: '#888'}}>
                    <Icon type="check-circle" style={{fontSize: '32px', color: '#52c41a'}} />
                    <div style={{marginTop: '8px'}}>
                        No contracts are due to renew within {report.horizon_months} month
                        {report.horizon_months === 1 ? '' : 's'} of{' '}
                        {moment(report.as_of_date).format('MMM DD, YYYY')}.
                    </div>
                  </Card>
                : <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey={(r, i) => `${r.contract_number}_${i}`}
                    pagination={false}
                    size="middle"
                    rowClassName={(r, i) => i % 2 ? 'row-alt' : ''} />}
        </div>}
    </div>;
}

export default withRouter(UpcomingContractRenewals);
